import React, { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { referralsService } from "services/referrals/referrals.service"
import { settingsService } from "services/settings/settings.service"
import { AffiliateCommissionMode } from "services/user/type"

import { formatFiatCurrency, isValidPercent, parseIntSafe } from "util/formatter"

import { ModalConfirmAffiliateAction } from "./ModalConfirmAffiliateAction"

export const GlobalAffiliateBulkEditor = () => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [mode, setMode] = useState<AffiliateCommissionMode | "">("")
  const [returnPercent, setReturnPercent] = useState("")

  const [cpaRewardCents, setCpaRewardCents] = useState("")
  const [cpaRequireKyc, setCpaRequireKyc] = useState<"any" | "yes" | "no">("any")
  const [cpaMinDepositCents, setCpaMinDepositCents] = useState("")
  const [cpaMinWagerCents, setCpaMinWagerCents] = useState("")

  const [saving, setSaving] = useState(false)

  const [defaults, setDefaults] = useState<{
    commission_mode: AffiliateCommissionMode | null
    return_percent: number | null
    cpa_config: any | null
  } | null>(null)
  const [defaultsLoading, setDefaultsLoading] = useState(false)

  const [confirmApplyAllOpen, setConfirmApplyAllOpen] = useState(false)
  const [confirmSaveDefaultOpen, setConfirmSaveDefaultOpen] = useState(false)

  const isCpaMode = mode === AffiliateCommissionMode.CPA

  const percentError = useMemo(() => {
    if (isCpaMode) return null
    if (!returnPercent.trim()) return null
    return isValidPercent(returnPercent) ? null : "return_percent must be between 0 and 100"
  }, [isCpaMode, returnPercent])

  const rewardError = useMemo(() => {
    if (!isCpaMode) return null
    if (!cpaRewardCents.trim()) return null
    const n = Number(cpaRewardCents)
    if (!Number.isFinite(n) || n < 0) return "reward_cents must be >= 0"
    return null
  }, [isCpaMode, cpaRewardCents])

  const payload = useMemo(() => {
    const patch: any = {}

    if (mode) patch.commission_mode = mode

    if (!isCpaMode) {
      if (returnPercent.trim()) {
        const n = Number(returnPercent)
        if (!Number.isNaN(n)) patch.return_percent = n
      }
      return patch
    }

    const cfg: any = {}
    const reward = parseIntSafe(cpaRewardCents)
    const minDep = parseIntSafe(cpaMinDepositCents)
    const minWager = parseIntSafe(cpaMinWagerCents)

    if (reward !== null) cfg.reward_cents = reward
    if (cpaRequireKyc !== "any") cfg.require_kyc = cpaRequireKyc === "yes"
    if (minDep !== null) cfg.min_total_deposit_cents = minDep
    if (minWager !== null) cfg.min_total_wager_cents = minWager

    if (Object.keys(cfg).length) patch.cpa_config = cfg

    return patch
  }, [mode, isCpaMode, returnPercent, cpaRewardCents, cpaRequireKyc, cpaMinDepositCents, cpaMinWagerCents])

  const hasAny = useMemo(() => {
    return payload.commission_mode !== undefined || payload.return_percent !== undefined || payload.cpa_config !== undefined
  }, [payload])

  const canSubmit = !saving && hasAny && !percentError && !rewardError

  const validateOrToast = () => {
    if (!hasAny) {
      toast.error("Nothing to update")
      return false
    }
    if (percentError) {
      toast.error(percentError)
      return false
    }
    if (rewardError) {
      toast.error(rewardError)
      return false
    }
    return true
  }

  const openApplyAllConfirm = () => {
    if (!validateOrToast()) return
    setConfirmApplyAllOpen(true)
  }

  const openSaveDefaultConfirm = () => {
    if (!validateOrToast()) return
    setConfirmSaveDefaultOpen(true)
  }

  const applyAllConfirmed = async () => {
    if (!validateOrToast()) return
    try {
      setSaving(true)
      const res = await referralsService.updateAllAffiliatesBulk(payload)
      const updated = res?.payload?.updated ?? res?.updated
      toast.success(`Updated ${updated ?? ""} affiliate code(s)`)
      setConfirmApplyAllOpen(false)
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to bulk update affiliates")
    } finally {
      setSaving(false)
    }
  }

  const saveDefaultConfirmed = async () => {
    if (!validateOrToast()) return
    try {
      setSaving(true)
      const res = await settingsService.saveAffiliateDefaults(payload)
      toast.success(res?.message ?? "Saved as default for new affiliate codes")
      setConfirmSaveDefaultOpen(false)

      if (res?.defaults) setDefaults(res.defaults)
      else {
        const d = await settingsService.getAffiliateDefaults()
        setDefaults(d ?? null)
      }
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to save defaults")
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        setDefaultsLoading(true)
        const d = await settingsService.getAffiliateDefaults()
        if (mounted) setDefaults(d ?? null)
      } catch (e: any) {
        toast.error(e?.data?.message ?? e?.message ?? "Failed to load affiliate defaults")
      } finally {
        if (mounted) setDefaultsLoading(false)
      }
    })()

    return () => {
      mounted = false
    }
  }, [])

  return (
    <>
      <div className="rounded-xl border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-1">
            <div className="text-base font-semibold">{t("affiliate.globalSettings")}</div>
            <div className="text-sm opacity-60">{t("affiliate.applyToAllNote")}</div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className={clsx("btn btn-outline btn-sm", { "btn-disabled": !canSubmit })}
              onClick={openSaveDefaultConfirm}
              disabled={!canSubmit}
            >
              {t("affiliate.saveAsDefault")}
            </button>

            <button
              type="button"
              className={clsx("btn btn-primary btn-sm", { "btn-disabled": !canSubmit })}
              onClick={openApplyAllConfirm}
              disabled={!canSubmit}
            >
              {saving ? <span className="loading loading-spinner loading-xs" /> : null}
              {t("affiliate.applyToAll")}
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm opacity-80">{t("affiliate.commissionMode")}</label>
            <select
              className="select select-bordered w-full"
              value={mode}
              onChange={(e) => setMode((e.target.value as AffiliateCommissionMode) || "")}
              disabled={saving}
            >
              <option value="">{t("affiliate.doNotChange")}</option>
              <option value={AffiliateCommissionMode.RAKE}>RAKE</option>
              <option value={AffiliateCommissionMode.NGR}>NGR</option>
              <option value={AffiliateCommissionMode.CPA}>CPA</option>
            </select>
            <div className="text-xs opacity-60">{t("affiliate.leaveEmpty")}</div>
          </div>

          {!isCpaMode ? (
            <div className="flex flex-col gap-1">
              <label className="text-sm opacity-80">{t("affiliate.returnPercent")}</label>
              <input
                className={clsx("input input-bordered w-full", { "input-error": !!percentError })}
                value={returnPercent}
                onChange={(e) => setReturnPercent(e.target.value)}
                placeholder="0 - 100"
                inputMode="decimal"
                disabled={saving}
              />
              <div className={clsx("text-xs", percentError ? "text-error" : "opacity-60")}>{percentError ?? t("affiliate.leaveEmpty")}</div>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <label className="text-sm opacity-80">
                {t("affiliate.rewardCents")} {formatFiatCurrency(cpaRewardCents)}
              </label>
              <input
                className={clsx("input input-bordered w-full", { "input-error": !!rewardError })}
                value={cpaRewardCents}
                onChange={(e) => setCpaRewardCents(e.target.value)}
                placeholder={t("affiliate.rewardPlaceholder") as string}
                inputMode="numeric"
                disabled={saving}
              />
              <div className={clsx("text-xs", rewardError ? "text-error" : "opacity-60")}>{rewardError ?? t("affiliate.leaveEmpty")}</div>
            </div>
          )}

          {isCpaMode ? (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-sm opacity-80">{t("affiliate.requireKyc")}</label>
                <select
                  className="select select-bordered w-full"
                  value={cpaRequireKyc}
                  onChange={(e) => setCpaRequireKyc(e.target.value as any)}
                  disabled={saving}
                >
                  <option value="any">{t("affiliate.any")}</option>
                  <option value="yes">{t("common.yes")}</option>
                  <option value="no">{t("common.no")}</option>
                </select>
                <div className="text-xs opacity-60">{t("affiliate.eligibilityCondition")}</div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm opacity-80">
                  {t("affiliate.minTotalDeposit")} {formatFiatCurrency(cpaMinDepositCents)}
                </label>
                <input
                  className="input input-bordered w-full"
                  value={cpaMinDepositCents}
                  onChange={(e) => setCpaMinDepositCents(e.target.value)}
                  inputMode="numeric"
                  placeholder={t("affiliate.minDepositPlaceholder") as string}
                  disabled={saving}
                />
                <div className="text-xs opacity-60">{t("affiliate.leaveEmpty")}</div>
              </div>

              <div className="flex flex-col gap-1 md:col-span-1">
                <label className="text-sm opacity-80">
                  {t("affiliate.minTotalWager")} {formatFiatCurrency(cpaMinWagerCents)}
                </label>
                <input
                  className="input input-bordered w-full"
                  value={cpaMinWagerCents}
                  onChange={(e) => setCpaMinWagerCents(e.target.value)}
                  inputMode="numeric"
                  placeholder={t("affiliate.minWagerPlaceholder") as string}
                  disabled={saving}
                />
                <div className="text-xs opacity-60">{t("affiliate.leaveEmpty")}</div>
              </div>
            </>
          ) : null}
        </div>

        <div className="mt-3 rounded-lg bg-[rgba(0,0,0,0.03)] dark:bg-[rgba(255,255,255,0.06)] p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium">{t("affiliate.currentDefaults")}</div>

            <button
              type="button"
              className={clsx("btn btn-ghost btn-xs", { "btn-disabled": saving || defaultsLoading })}
              disabled={saving || defaultsLoading}
              onClick={async () => {
                try {
                  setDefaultsLoading(true)
                  const d = await settingsService.getAffiliateDefaults()
                  setDefaults(d ?? null)
                } catch (e: any) {
                  toast.error(e?.data?.message ?? e?.message ?? "Failed to refresh defaults")
                } finally {
                  setDefaultsLoading(false)
                }
              }}
            >
              {defaultsLoading ? <span className="loading loading-spinner loading-xs" /> : null}
              {t("common.refresh")}
            </button>
          </div>

          <div className="mt-2 text-sm opacity-80">
            {!defaults && !defaultsLoading ? (
              <span className="opacity-60">{t("affiliate.noDefaultsSaved")}</span>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <span className="opacity-60">{t("affiliate.mode")}:</span>{" "}
                  <span className="font-medium">{defaults?.commission_mode ?? "—"}</span>
                </div>

                <div>
                  <span className="opacity-60">{t("affiliate.returnPercentLabel")}:</span>{" "}
                  <span className="font-medium">{defaults?.return_percent ?? "—"}</span>
                </div>

                <div>
                  <span className="opacity-60">{t("affiliate.cpaLabel")}:</span>{" "}
                  <span className="font-medium">
                    {defaults?.cpa_config
                      ? `reward=${defaults.cpa_config.reward_cents ?? 0} (${formatFiatCurrency(
                          defaults.cpa_config.reward_cents
                        )}), kyc=${String(defaults.cpa_config.require_kyc ?? true)}, minDep=${
                          defaults.cpa_config.min_total_deposit_cents ?? 0
                        } (${formatFiatCurrency(defaults.cpa_config.min_total_deposit_cents)}), minWager=${
                          defaults.cpa_config.min_total_wager_cents ?? 0
                        } (${formatFiatCurrency(defaults.cpa_config.min_total_wager_cents)})`
                      : "—"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ModalConfirmAffiliateAction
        open={confirmApplyAllOpen}
        onClose={() => (saving ? null : setConfirmApplyAllOpen(false))}
        onConfirm={applyAllConfirmed}
        loading={saving}
        title={t("affiliate.applyAllTitle")}
        description={
          <div className="space-y-2">
            <div>{t("affiliate.applyAllDescription")}</div>
            <div className="text-warning">{t("affiliate.confirmTitle")}</div>
          </div>
        }
        confirmText={t("affiliate.applyAllConfirm")}
      />

      <ModalConfirmAffiliateAction
        open={confirmSaveDefaultOpen}
        onClose={() => (saving ? null : setConfirmSaveDefaultOpen(false))}
        onConfirm={saveDefaultConfirmed}
        loading={saving}
        title={t("affiliate.saveDefaultTitle")}
        description={t("affiliate.saveDefaultDescription")}
        confirmText={t("affiliate.saveDefaultConfirm")}
      />
    </>
  )
}
